import { types, flow, getSnapshot } from 'mobx-state-tree';
import { Meeting } from './Meeting';
import { ApiStore } from './ApiStore';

const _meetingStore = types.model('MeetingStore', {
    meetingTypeKey: types.maybe(types.string),
    meeting: types.maybe(Meeting),
    documentKey: types.optional(types.string, '')
})

export const MeetingStore = types.compose(_meetingStore, ApiStore)
    // Monotonic launch counter: every session-initiating action bumps it, and
    // the async pollers below abort when it moves on. Guarding on
    // meetingTypeKey alone couldn't detect a switch BETWEEN the three L3 types
    // (they all set 'qa_practice'), so a stale Document-Defense poll could
    // clobber a live Messaging-Defense session with the wrong question set.
    .volatile(() => ({ activeLaunchId: 0 }))
    .actions(self => ({
        setMeetingType: flow(function* setMeetingType(meetingTypeKey, companyId) {
            self.activeLaunchId += 1;
            self.meetingTypeKey = meetingTypeKey;

            // Clear Document Defense state so it doesn't leak into standard pitches
            self.documentKey = '';

            const url = `/murphy/meeting?meetingTypeKey=${meetingTypeKey}&companyId=${companyId}`;

            let response;
            try {
                response = yield self.getApi(url);

                self.meetingTypeKey = meetingTypeKey;

                // Always create a fresh meeting object instead of updating existing one
                // This ensures no stale state persists between meeting type changes
                self.meeting = Meeting.create(response);

            } catch (error) {
                console.error('Murphy meeting construction failed!', error.message);
                // Drop any PREVIOUS meeting and rethrow. Swallowing here made
                // every "Start Recording" caller navigate as if construction
                // succeeded: first-ever session bounced back with no error,
                // and later sessions reused the prior meeting — the recording
                // screen then ran (and GRADED) the wrong session type.
                self.meeting = undefined;
                throw error;
            }

            return self.meeting;
        }),
        getInteractionSnapshots() {
            // Safely get snapshots of current interactions
            if (!self.meeting || !self.meeting.interactions) {
                return [];
            }

            return self.meeting.interactions.map(interaction => {
                try {
                    return getSnapshot(interaction);
                } catch (error) {
                    // Fallback for detached models
                    return {
                        duration: interaction.duration,
                        text: interaction.text,
                        startTime: interaction.startTime,
                        participantName: interaction.participantName
                    };
                }
            });
        },
        // Document Defense Library: the session row was already written server-side
        // (GET /companies/{id}/library/documents/{docId}/start-session returns the
        // same payload /murphy/doc-qa-status returns when ready), so there is
        // nothing to poll. Builds the Meeting exactly as initDocumentQASession does
        // and bumps the launch counter so any in-flight L3 poller aborts.
        loadReadyQASession(payload) {
            self.activeLaunchId += 1;
            self.meetingTypeKey = 'qa_practice';
            // Deliberately EMPTY. A non-empty documentKey arms NewRecordPresentation's
            // leave-time full page reload, which exists to wipe an UPLOADED file from
            // browser memory in the one-off flow. A library session never holds the
            // file client-side (only question text, like Objection Handling), so the
            // reload only put a full app reload in front of the feedback.
            self.documentKey = '';

            const participants = (payload.participants || []).map(p => ({
                name: p.name,
                avatar: p.avatar || undefined,
                avatarColor: p.avatarColor || undefined
            }));
            const questions = (payload.questions || []).map(q => ({
                index: q.index,
                rephrasedQuestion: q.rephrasedQuestion,
                participantName: q.participantName,
                relatedTo: q.relatedTo || null
            }));

            self.meeting = Meeting.create({
                meetingTypeKey: 'qa_practice',
                level: 3,
                sessionId: payload.sessionId,
                questions: questions,
                questionCount: payload.questionCount || questions.length,
                participants: participants,
                interactions: []
            });
            return self.meeting;
        },
        generateAudio: flow(function* generateAudio(questionText, participantName) {
            try {
                return yield self.getApi(`/murphy/audio?text=${encodeURIComponent(questionText)}&participantName=${encodeURIComponent(participantName)}`);
            } catch (error) {
                console.warn('Murphy audio generation failed, using fallback for development:', error.message);
                // Return null so the component can handle gracefully
                return null;
            }
        }),
        // Initialize Q&A Practice session with questions from company Q&A library
        initQASession: flow(function* initQASession(companyId, questionCount = 5) {
            self.activeLaunchId += 1;
            self.meetingTypeKey = 'qa_practice';

            // Clear Document Defense state — Objection Handling is a different flow
            self.documentKey = '';

            const url = `/murphy/qa-session?companyId=${companyId}&questionCount=${questionCount}`;

            try {
                const response = yield self.getApi(url);
                console.log('MeetingStore: Q&A session initialized', response);

                // Create meeting object with Q&A data
                // Filter participant fields to match the Participant model (MST rejects extra props)
                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                // Filter question fields to match the Question model
                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Q&A session initialization failed!', error.message);
                throw error;
            }
        }),
        // Initialize Document Defense session with questions generated from an uploaded document
        // Uses async pattern: GET to start generation, then poll for results
        initDocumentQASession: flow(function* initDocumentQASession(companyId, questionCount = 5, documentKey) {
            self.activeLaunchId += 1;
            const myLaunchId = self.activeLaunchId;
            self.meetingTypeKey = 'qa_practice';
            self.documentKey = documentKey;

            try {
                // Step 1: Kick off async generation via GET with query params
                const qs = `companyId=${encodeURIComponent(companyId)}&questionCount=${questionCount}&documentKey=${encodeURIComponent(documentKey)}`;
                const initResponse = yield self.getApi(`/murphy/doc-qa-session?${qs}`);
                console.log('MeetingStore: Document Defense generation started', initResponse);
                const { sessionId } = initResponse;

                // Step 2: Poll for results (every 3s, up to 2 minutes)
                const maxAttempts = 40;
                let response = null;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    // Bail out if the user launched ANY other session (including
                    // another L3 type — they all share meetingTypeKey 'qa_practice')
                    if (self.activeLaunchId !== myLaunchId) {
                        console.log('MeetingStore: Document Defense poll aborted — user launched another session');
                        return null;
                    }
                    yield new Promise(resolve => setTimeout(resolve, 3000));
                    const status = yield self.getApi(`/murphy/doc-qa-status?sessionId=${sessionId}`);
                    console.log(`MeetingStore: Poll attempt ${attempt + 1}/${maxAttempts}, status: ${status.status}`);

                    if (status.status === 'ready') {
                        response = status;
                        break;
                    }
                    if (status.status === 'error') {
                        const isOverloaded = status.error && (status.error.includes('529') || status.error.includes('overloaded') || status.error.includes('Overloaded'));
                        // The server explains an unrelated document in words
                        // meant for the user; show that instead of "try again".
                        const isUnrelated = status.error && /doesn't appear to relate to|couldn't find any readable text|couldn't open this file/.test(status.error);
                        throw new Error(isOverloaded
                            ? 'Murphy is temporarily busy — please try again in a moment.'
                            : isUnrelated
                                ? status.error
                                : 'Failed to prepare questions from the document. Please try again.');
                    }
                }

                if (!response) {
                    throw new Error('Question generation timed out. Please try again.');
                }

                // Final guard: don't overwrite if the user launched another session
                if (self.activeLaunchId !== myLaunchId) {
                    console.log('MeetingStore: Document Defense ready but user launched another session — discarding');
                    return null;
                }

                console.log('MeetingStore: Document Defense session ready', response);

                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Document Defense session initialization failed!', error.message);
                throw error;
            }
        }),
        // Initialize Messaging Defense session — Murphy generates objections from company profile
        // Uses async pattern (same as Document Defense): GET to start, then poll for results
        initProspectPrepSession: flow(function* initProspectPrepSession(companyId, questionCount = 5) {
            self.activeLaunchId += 1;
            const myLaunchId = self.activeLaunchId;
            self.meetingTypeKey = 'qa_practice';
            self.documentKey = '';

            try {
                const qs = `companyId=${encodeURIComponent(companyId)}&questionCount=${questionCount}`;
                const initResponse = yield self.getApi(`/murphy/prospect-prep?${qs}`);
                console.log('MeetingStore: Messaging Defense generation started', initResponse);
                const { sessionId } = initResponse;

                const maxAttempts = 40;
                let response = null;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    if (self.activeLaunchId !== myLaunchId) {
                        console.log('MeetingStore: Messaging Defense poll aborted — user launched another session');
                        return null;
                    }
                    yield new Promise(resolve => setTimeout(resolve, 3000));
                    const status = yield self.getApi(`/murphy/prospect-prep-status?sessionId=${sessionId}`);
                    console.log(`MeetingStore: Messaging Defense poll attempt ${attempt + 1}/${maxAttempts}, status: ${status.status}`);

                    if (status.status === 'ready') {
                        response = status;
                        break;
                    }
                    if (status.status === 'error') {
                        const isOverloaded = status.error && (status.error.includes('529') || status.error.includes('overloaded') || status.error.includes('Overloaded'));
                        throw new Error(isOverloaded
                            ? 'Murphy is temporarily busy — please try again in a moment.'
                            : 'Failed to generate prospect prep questions. Please try again.');
                    }
                }

                if (!response) {
                    throw new Error('Question generation timed out. Please try again.');
                }

                if (self.activeLaunchId !== myLaunchId) {
                    console.log('MeetingStore: Messaging Defense ready but user launched another session — discarding');
                    return null;
                }

                console.log('MeetingStore: Messaging Defense session ready', response);

                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Messaging Defense session initialization failed!', error.message);
                throw error;
            }
        }),
        // With a launchId, only clears if that launch is still the current
        // one, so a late call can never wipe a session started after it.
        clearDocumentSession(launchId) {
            if (launchId !== undefined && launchId !== self.activeLaunchId) return;
            self.documentKey = '';
            self.meeting = undefined;
        }
    }));